/**
 * @description Performance monitoring tool
 * Used to monitor the loading performance of lazy-loaded components
 */

// Extend Window interface to include performanceMonitor property
declare global {
  interface Window {
    performanceMonitor?: PerformanceMonitor;
  }
}

interface LoadingMetrics {
  componentName: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  success: boolean;
  error?: string;
}

class PerformanceMonitor {
  private metrics: LoadingMetrics[] = [];
  private isEnabled: boolean;

  constructor() {
    // Only enable performance monitoring in development environment
    this.isEnabled = process.env.NODE_ENV === "development";
  }

  /**
   * start monitoring component loading
   */
  startLoading(componentName: string): string {
    if (!this.isEnabled) return "";

    const id = `${componentName}_${Date.now()}`;
    const metric: LoadingMetrics = {
      componentName,
      startTime: performance.now(),
      success: false,
    };

    this.metrics.push(metric);
    console.log(`🚀 Starting to load component: ${componentName}`);

    return id;
  }

  /**
   * end monitoring component loading
   */
  endLoading(componentName: string, success: boolean = true, error?: string): void {
    if (!this.isEnabled) return;

    const metric = this.metrics
      .slice()
      .reverse()
      .find((m) => m.componentName === componentName && !m.endTime);

    if (metric) {
      metric.endTime = performance.now();
      metric.duration = metric.endTime - metric.startTime;
      metric.success = success;
      metric.error = error;

      if (success) {
        console.log(
          `✅ Component loaded successfully: ${componentName} (${metric.duration.toFixed(2)}ms)`,
        );
      } else {
        console.error(
          `❌ Component loading failed: ${componentName} (${metric.duration.toFixed(2)}ms)`,
          error,
        );
      }
    }
  }

  /**
   * @description Get performance report
   */
  getReport(): {
    totalComponents: number;
    successfulLoads: number;
    failedLoads: number;
    averageLoadTime: number;
    slowestComponent: LoadingMetrics | null;
    fastestComponent: LoadingMetrics | null;
  } {
    const completedMetrics = this.metrics.filter((m) => m.endTime && m.duration);
    const successfulMetrics = completedMetrics.filter((m) => m.success);
    const failedMetrics = completedMetrics.filter((m) => !m.success);

    const averageLoadTime =
      successfulMetrics.length > 0
        ? successfulMetrics.reduce((sum, m) => sum + (m.duration || 0), 0) /
          successfulMetrics.length
        : 0;

    const slowestComponent =
      successfulMetrics.length > 0
        ? successfulMetrics.reduce((slowest, current) =>
            (current.duration || 0) > (slowest.duration || 0) ? current : slowest,
          )
        : null;

    const fastestComponent =
      successfulMetrics.length > 0
        ? successfulMetrics.reduce((fastest, current) =>
            (current.duration || 0) < (fastest.duration || 0) ? current : fastest,
          )
        : null;

    return {
      totalComponents: completedMetrics.length,
      successfulLoads: successfulMetrics.length,
      failedLoads: failedMetrics.length,
      averageLoadTime,
      slowestComponent,
      fastestComponent,
    };
  }

  /**
   * @description Print performance report to console
   */
  printReport(): void {
    if (!this.isEnabled) return;

    const report = this.getReport();

    console.group("📊 Lazy Loading Performance Report");
    console.log(`Total components: ${report.totalComponents}`);
    console.log(`Successful loads: ${report.successfulLoads}`);
    console.log(`Failed loads: ${report.failedLoads}`);
    console.log(`Average load time: ${report.averageLoadTime.toFixed(2)}ms`);

    if (report.slowestComponent) {
      console.log(
        `Slowest component: ${report.slowestComponent.componentName} (${report.slowestComponent.duration?.toFixed(2)}ms)`,
      );
    }

    if (report.fastestComponent) {
      console.log(
        `Fastest component: ${report.fastestComponent.componentName} (${report.fastestComponent.duration?.toFixed(2)}ms)`,
      );
    }

    console.groupEnd();
  }

  /**
   * @description Clear all monitoring data
   */
  clear(): void {
    this.metrics = [];
  }
}

// Create global instance
export const performanceMonitor = new PerformanceMonitor();

// Expose monitor to global object in development environment for debugging
if (process.env.NODE_ENV === "development") {
  window.performanceMonitor = performanceMonitor;

  // Print report when page unloads
  window.addEventListener("beforeunload", () => {
    performanceMonitor.printReport();
  });
}

export default performanceMonitor;
